import { faFloppyDisk, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import httpRequest from "src/helpers/httpRequest";
import { IHomecareService } from "src/types/GlobalInterfaces";
import SimpleInput from "../SimpleInput";
import SelectDropdown from "../GlobalComponents/SelectDropdown";
import PrimaryBtn from "../GlobalComponents/primaryBtn";
import ReactQuill from "react-quill";
import ImageUpload from "../GlobalComponents/ImageUplaod";
import { convertToKebabCase } from "src/helpers/helperFunctions";
import CustomWebsiteDropdown from "../GlobalComponents/Widget/CustomWebsiteDropdown";

interface IProps {
  isEdit: string | null;
  onSuccess: () => void;
}

interface IHomecareServiceCreate {
  serviceName: string;
  category: string[];
  shortDescription: string;
  prescriptionRequired: boolean;
  serviceDuration: string;
  healthCareProvider: string;
  overview: string;
  price: string;
  image: File | null | string;
  status: boolean;
}

type tabKeys = "General" | "Highlights" | "Overview" | "Price" | "Image";

const serviceDuration: { value: string; label: string }[] = [
  { value: "1Hour", label: "1 Hour" },
  { value: "2Hour", label: "2 Hour" },
  { value: "3Hour", label: "3 Hour" },
];

const healthcareProviders: { value: string; label: string }[] = [
  { value: "Registered Nurse", label: "Registered Nurse" },
  { value: "Registered Physiotherapist", label: "Registered Physiotherapist" },
];

const UpsertHomecareServices: React.FC<IProps> = ({
  isEdit = null,
  onSuccess = () => {},
}) => {
  const tabsArray: tabKeys[] = [
    "General",
    "Highlights",
    "Overview",
    "Price",
    "Image",
  ];
  const [activeTab, setActiveTab] = useState<tabKeys>("General");
  const handleTabClick = (tabName: tabKeys) => {
    setActiveTab(tabName);
  };

  const [isLoader, setIsLoader] = useState<boolean>(false);
  const [payload, setPayload] = useState<IHomecareServiceCreate>({
    serviceName: "",
    category: [],
    shortDescription: "",
    prescriptionRequired: false,
    serviceDuration: "",
    healthCareProvider: "",
    overview: "",
    price: "",
    image: null,
    status: true,
  });
  const [errors, setErrors] = useState<{
    [key: string]: string;
  }>({});
  const [isValidTabs, setIsValidTabs] = useState<{
    [key: string]: boolean;
  }>({
    General: false,
    Highlights: false,
    Overview: false,
    Price: false,
    Image: false,
  });

  const validateGeneralDetails = (onlyLength: boolean = false) => {
    const newErrors: { [key: string]: string } = {};
    if (!payload.serviceName) {
      newErrors.serviceName = "Service name is required";
    }
    if (!payload.category.length) {
      newErrors.category = "At least one category is required";
    }
    if (!payload.shortDescription) {
      newErrors.shortDescription = "Short description is required";
    }
    if (onlyLength) {
      return Object.keys(newErrors).length === 0;
    } else {
      setErrors(newErrors);
    }
    return Object.keys(newErrors).length === 0;
  };

  const validateHighlightsDetails = (onlyLength: boolean = false) => {
    const newErrors: { [key: string]: string } = {};
    if (!payload.serviceDuration) {
      newErrors.serviceDuration = "Service duration is required";
    }
    if (!payload.healthCareProvider) {
      newErrors.healthCareProvider = "Healthcare provider is required";
    }
    if (onlyLength) {
      return Object.keys(newErrors).length === 0;
    } else {
      setErrors(newErrors);
    }
    return Object.keys(newErrors).length === 0;
  };

  const validateOverviewDetails = (onlyLength: boolean = false) => {
    const newErrors: { [key: string]: string } = {};
    if (!payload.overview) {
      newErrors.overview = "Overview is required";
    }
    if (onlyLength) {
      return Object.keys(newErrors).length === 0;
    } else {
      setErrors(newErrors);
    }
    return Object.keys(newErrors).length === 0;
  };

  const validatePriceDetails = (onlyLength: boolean = false) => {
    const newErrors: { [key: string]: string } = {};
    if (!payload.price) {
      newErrors.price = "Price is required";
    }
    if (parseInt(payload.price) <= 0) {
      newErrors.price = "Price must be greater than 0";
    }
    if (onlyLength) {
      return Object.keys(newErrors).length === 0;
    } else {
      setErrors(newErrors);
    }
    return Object.keys(newErrors).length === 0;
  };

  const validateImageDetails = (onlyLength: boolean = false) => {
    const newErrors: { [key: string]: string } = {};
    if (!payload.image) {
      newErrors.image = "Image is required";
    }
    if (onlyLength) {
      return Object.keys(newErrors).length === 0;
    } else {
      setErrors(newErrors);
    }
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    let chcekAll =
      validateGeneralDetails(true) &&
      validateHighlightsDetails(true) &&
      validateOverviewDetails(true) &&
      validatePriceDetails(true) &&
      validateImageDetails(true);
    setIsValidTabs({
      General: true,
      Highlights: validateGeneralDetails(true),
      Overview: validateHighlightsDetails(true),
      Price: validateOverviewDetails(true),
      Image: validatePriceDetails(true),
    });
  }, [payload]);

  const handelChange = (
    key: keyof IHomecareServiceCreate,
    val: string | number | boolean
  ) => {
    setPayload((prev) => ({
      ...prev,
      [key]: val,
    }));
    setErrors((prev) => ({
      ...prev,
      [key]: "",
    }));
  };

  const handleSubmit = async () => {
    const {
      serviceName,
      category,
      shortDescription,
      prescriptionRequired,
      status,
      serviceDuration,
      healthCareProvider,
      overview,
      price,
      image,
    } = payload;
    if (!validateGeneralDetails()) {
      return;
    }

    if (!validateHighlightsDetails()) {
      return;
    }

    if (!validateOverviewDetails()) {
      return;
    }

    if (!validatePriceDetails()) {
      return;
    }

    if (!validateImageDetails()) {
      return;
    }

    if (isLoader) return;

    setIsLoader(true);
    const formData = new FormData();
    formData.append("serviceName", serviceName);
    // formData.append("category", JSON.stringify(category));
    category.forEach((v, i) => {
      formData.append(`category[${i}]`, v);
    });
    formData.append("shortDescription", shortDescription);
    formData.append("prescriptionRequired", String(prescriptionRequired));
    formData.append("status", String(status));
    formData.append("serviceDuration", serviceDuration);
    formData.append("healthCareProvider", healthCareProvider);
    formData.append("overview", overview);
    formData.append("price", price);
    formData.append("image", image as unknown as Blob);
    const path = isEdit
      ? `/home-care-services/edit/${isEdit}`
      : "/home-care-services/create";
    const { res, err, formErrors } = await httpRequest<{ message: string }>({
      method: isEdit ? "put" : "post",
      path,
      params: formData,
      header: { "Content-Type": "multipart/form-data" },
    });
    if (res) {
      toast(`Service ${isEdit ? "updated" : "added"} successfully.`, {
        type: "success",
      });
      onSuccess();
      setPayload({
        serviceName: "",
        category: [],
        shortDescription: "",
        prescriptionRequired: false,
        serviceDuration: "",
        healthCareProvider: "",
        overview: "",
        price: "",
        image: null,
        status: true,
      });
    } else {
      let errorMessage = err;
      toast(errorMessage, { type: "error" });
    }
    setIsLoader(false);
  };

  const [getLoader, setGetLoader] = useState<boolean>(false);
  const [allCategories, setAllCategories] = useState<
    { value: string; label: string }[]
  >([]);
  const fetchAllCategories = async () => {
    setGetLoader(true);
    const { res, err } = await httpRequest<
      { categoryName: string; _id: string }[]
    >({ path: "/homecare-category/all" });
    if (res) {
      setAllCategories(
        (res || []).map((v) => ({ label: v.categoryName, value: v._id }))
      );
    } else {
      toast(err, { type: "error" });
    }
    setGetLoader(false);
  };

  const [fetchLoader, setFetchLoader] = useState(false);
  const fetchById = async () => {
    setFetchLoader(true);
    const { res, err } = await httpRequest<IHomecareServiceCreate>({
      path: `/home-care-services/${isEdit}`,
    });
    if (res) {
      setPayload(res);
    } else {
      toast(err, { type: "error" });
    }
    setFetchLoader(false);
  };

  useEffect(() => {
    if (isEdit) {
      fetchById();
    }
  }, [isEdit]);

  useEffect(() => {
    fetchAllCategories();
  }, []);
  return (
    <div className="relative">
      <button
        className="absolute save-pharmacy-and-product border-[#333] border rounded"
        disabled={isLoader}
        onClick={handleSubmit}
        id="save-btn"
      >
        <FontAwesomeIcon
          spin={isLoader}
          className="text-sm"
          icon={isLoader ? faSpinner : faFloppyDisk}
        />
      </button>
      <div className="products-tabs-btn">
        {tabsArray.map((item: tabKeys) => (
          <PrimaryBtn
            onClick={() => handleTabClick(item)}
            ButtonText={item}
            disabled={!isValidTabs[item as keyof typeof isValidTabs]}
            disabledOpactiy={true}
            btnExtraStyle={{
              border: "1px solid",
              backgroundColor: activeTab === item ? "#00443f" : "transparent",
              color: activeTab === item ? "white" : "#00443f",
              borderColor: activeTab === item ? "transparent" : "#00443f",
              padding: "0.5rem 2rem",
            }}
          />
        ))}
      </div>
      <hr
        className="products-tabs-hr"
        style={{ border: "1px solid #D4D4D4" }}
      />
      <div className="parent-box-scroll-bar-product">
        <div className="products-tabs-content-main">
          {activeTab === "General" ? (
            <>
              <div className="products-tabs-content">
                <SimpleInput
                  value={payload?.serviceName}
                  onChange={(val) => handelChange("serviceName", val)}
                  isLabel={true}
                  labelText="Service Name"
                  maxLength={100}
                  id="service-name"
                  isRequired
                  error={errors.serviceName}
                />
                <CustomWebsiteDropdown
                  loading={getLoader}
                  options={allCategories}
                  isMulti={true}
                  data={allCategories.filter((v) =>
                    (payload?.category || []).includes(v?.value)
                  )}
                  setData={(e) => {
                    setPayload((prev) => ({
                      ...prev,
                      category: e.map((v) => v.value),
                    }));
                    setErrors((prev) => ({
                      ...prev,
                      category:  e.length ? "" : "At least one category is required",
                    } as any));
                  }}
                  labelText="Categories"
                  dropdownMainId="category-dropdown"
                  isRequired
                  error={errors.category}
                />

                <SimpleInput
                  value={payload?.shortDescription}
                  onChange={(val) => handelChange("shortDescription", val)}
                  isLabel={true}
                  labelText="Short Description"
                  type="textArea"
                  rows={5}
                  maxLength={500}
                  isLimit={500}
                  id="short-description"
                  isRequired
                  error={errors.shortDescription}
                />
                <div className="flex items-center gap-2">
                  <input
                    checked={payload?.prescriptionRequired || false}
                    type="checkbox"
                    id="prescriped"
                    onChange={() =>
                      handelChange(
                        "prescriptionRequired",
                        !payload.prescriptionRequired
                      )
                    }
                  />
                  <label
                    htmlFor="prescriped"
                    className="font-size-18px font-Poppins-Regular"
                  >
                    Prescription Required
                  </label>
                </div>
              </div>
              <div className="flex justify-end my-6">
                <PrimaryBtn
                  ButtonText="Next"
                  btnExtraStyle={{
                    backgroundColor: "#00443f",
                    borderRadius: "1.5rem",
                  }}
                  onPress={() => {
                    if (validateGeneralDetails()) {
                      handleTabClick("Highlights");
                    }
                  }}
                  id="next-btn"
                />
              </div>
            </>
          ) : activeTab === "Highlights" ? (
            <>
              <div className="products-tabs-content">
                <CustomWebsiteDropdown
                  options={serviceDuration}
                  isMulti={false}
                  data={serviceDuration.filter(
                    (v) => v.value === payload.serviceDuration
                  )}
                  setData={(e) =>
                    handelChange("serviceDuration", e[0]?.value || "")
                  }
                  labelText="Service Duration"
                  dropdownMainId="service-duration-dropdown"
                  isRequired
                  error={errors.serviceDuration}
                />

                <CustomWebsiteDropdown
                  options={healthcareProviders}
                  isMulti={false}
                  data={healthcareProviders.filter(
                    (v) => v.value === payload.healthCareProvider
                  )}
                  setData={(e) =>
                    handelChange("healthCareProvider", e[0]?.value || "")
                  }
                  labelText="Healthcare Provider"
                  dropdownMainId="healthcare-provider-dropdown"
                  isRequired
                  error={errors.healthCareProvider}
                />
              </div>
              <div className="flex justify-end my-6">
                <PrimaryBtn
                  ButtonText="Next"
                  btnExtraStyle={{
                    backgroundColor: "#00443f",
                    borderRadius: "1.5rem",
                  }}
                  onPress={() => {
                    if (validateHighlightsDetails()) {
                      handleTabClick("Overview");
                    }
                  }}
                  id="next-btn"
                />
              </div>
            </>
          ) : activeTab === "Overview" ? (
            <>
              <div className="products-tabs-content pharma__inventory__overview">
                <ReactQuill
                  theme="snow"
                  style={{ backgroundColor: "#fff" }}
                  value={payload?.overview}
                  onChange={(txt: string) => handelChange("overview", txt)}
                  id="overview"
                />
                {errors.overview && (
                  <p
                    className={`text-red-500 font-size-14px font-Poppins-Regular`}
                  >
                    {errors.overview}
                  </p>
                )}
              </div>
              <div className="flex justify-end my-6">
                <PrimaryBtn
                  ButtonText="Next"
                  btnExtraStyle={{
                    backgroundColor: "#00443f",
                    borderRadius: "1.5rem",
                  }}
                  onPress={() => {
                    if (validateOverviewDetails()) {
                      handleTabClick("Price");
                    }
                  }}
                  id="next-btn"
                />
              </div>
            </>
          ) : activeTab === "Price" ? (
            <>
              <div className="products-tabs-content">
                <SimpleInput
                  type="number"
                  value={payload?.price}
                  onChange={(val) => {
                    if (val === "") {
                      handelChange("price", "");
                      return;
                    }
                    let newvalue = parseFloat(val || "0").toString();
                    handelChange("price", newvalue);
                  }}
                  isLabel={true}
                  labelText="Price"
                  id="price"
                  isRequired
                  error={errors.price}
                  maxLength={10}
                />
              </div>
              <div className="flex justify-end my-6">
                <PrimaryBtn
                  ButtonText="Next"
                  btnExtraStyle={{
                    backgroundColor: "#00443f",
                    borderRadius: "1.5rem",
                  }}
                  onPress={() => {
                    if (validatePriceDetails()) {
                      handleTabClick("Image");
                    }
                  }}
                  id="next-btn"
                />
              </div>
            </>
          ) : activeTab === "Image" ? (
            <>
              <div className="products-tabs-content">
                <h3 className="font-size-18px text-[#2F2F31] mb-1 font-Poppins-Regular">
                  <span className="text-[#FF0000]">* </span> Main Image
                </h3>
                <ImageUpload
                  image={payload?.image}
                  onImageChange={(file: File | null) =>
                    setPayload((prev) => ({ ...prev, image: file }))
                  }
                  extraClasses={"w-full min-h-[200px]"}
                  labelId="main-image"
                />
                {errors.image && (
                  <p
                    className={`text-red-500 font-size-14px font-Poppins-Regular`}
                  >
                    {errors.image}
                  </p>
                )}
              </div>
              <div className="flex justify-end my-6">
                <PrimaryBtn
                  ButtonText="Back"
                  btnExtraStyle={{
                    backgroundColor: "#00443f",
                    borderRadius: "1.5rem",
                  }}
                  onPress={() => handleTabClick("Price")}
                  id="back-btn"
                />
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default UpsertHomecareServices;
